import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Package,
  CreditCard,
  MapPin,
  Clock,
  CheckCircle,
  Truck,
  XCircle,
  AlertCircle,
  Download,
} from 'lucide-react';
import { useOrder } from '@/hooks/useProducts';
import { formatCurrency, formatDate, formatDateShort } from '@/lib/utils';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';

type OrderStatus =
  | 'pending'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

const statusVariants: Record<
  OrderStatus,
  'green' | 'yellow' | 'red' | 'blue' | 'gray'
> = {
  pending: 'yellow',
  processing: 'blue',
  shipped: 'blue',
  delivered: 'green',
  cancelled: 'red',
  refunded: 'gray',
};

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('account');
  const variant = statusVariants[status as OrderStatus] || 'gray';
  const key = `orders.statuses.${status}` as const;
  const translated = t(key);
  const label = translated === key ? status : translated;
  return <Badge variant={variant}>{label}</Badge>;
}

const TIMELINE_STEP_KEYS = [
  { key: 'pending', labelKey: 'placed', icon: Clock },
  { key: 'processing', labelKey: 'processing', icon: Package },
  { key: 'shipped', labelKey: 'shipped', icon: Truck },
  { key: 'delivered', labelKey: 'delivered', icon: CheckCircle },
] as const;

function getStepIndex(status: string): number {
  const map: Record<string, number> = {
    pending: 0,
    processing: 1,
    shipped: 2,
    delivered: 3,
  };
  return map[status] ?? -1;
}

interface AddressBlockProps {
  address: any;
}

function AddressBlock({ address }: AddressBlockProps) {
  if (!address) return null;
  return (
    <address className="text-sm text-ink-soft not-italic leading-relaxed">
      {address.first_name} {address.last_name}
      <br />
      {address.address_line1}
      {address.address_line2 && (
        <>
          <br />
          {address.address_line2}
        </>
      )}
      <br />
      {address.city}, {address.state} {address.postal_code}
      {address.phone && (
        <>
          <br />
          {address.phone}
        </>
      )}
    </address>
  );
}

export default function OrderDetail() {
  const { t } = useTranslation('account');
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading, isError, refetch } = useOrder(id || '');
  const addToast = useUIStore((s) => s.addToast);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const canCancel =
    order &&
    (order.status === 'pending' || order.status === 'processing');

  async function handleCancelOrder() {
    if (!id) return;
    setIsCancelling(true);
    try {
      await api.post(`/orders/${id}/cancel`);
      addToast(t('orderDetail.toasts.cancelled'), 'success');
      setIsCancelModalOpen(false);
      refetch();
    } catch (err: any) {
      addToast(err.message || t('orderDetail.toasts.cancelFailed'), 'error');
    } finally {
      setIsCancelling(false);
    }
  }

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (isError || !order) {
    return (
      <div className="text-center py-16">
        <AlertCircle
          className="h-12 w-12 text-ink-muted mx-auto mb-3"
          aria-hidden="true"
        />
        <p className="text-ink-muted mb-4">
          {t('orderDetail.errorLoading')}
        </p>
        <Link to="/account/orders">
          <Button variant="outline">{t('orderDetail.backToOrders')}</Button>
        </Link>
      </div>
    );
  }

  const currentStepIndex = getStepIndex(order.status);
  const isCancelledOrRefunded =
    order.status === 'cancelled' || order.status === 'refunded';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Link
            to="/account/orders"
            className="text-sm text-sage-700 hover:text-sage-800 inline-flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('orderDetail.backToOrders')}
          </Link>
          <h2 className="text-xl font-display font-bold text-ink">
            {t('orderDetail.orderHeading', { number: order.order_number })}
          </h2>
        </div>
        {canCancel && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setIsCancelModalOpen(true)}
          >
            <XCircle className="h-4 w-4" aria-hidden="true" />
            {t('orderDetail.cancelOrder')}
          </Button>
        )}
      </div>

      {/* Order Info Bar */}
      <Card>
        <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
          <div>
            <span className="text-ink-muted">{t('orderDetail.info.datePlaced')}</span>
            <p className="font-medium text-ink">
              {formatDate(order.created_at)}
            </p>
          </div>
          <div>
            <span className="text-ink-muted">{t('orderDetail.info.status')}</span>
            <p className="mt-0.5"><StatusBadge status={order.status} /></p>
          </div>
          <div>
            <span className="text-ink-muted">{t('orderDetail.info.total')}</span>
            <p className="font-semibold text-ink">
              {formatCurrency(Number(order.total))}
            </p>
          </div>
          {order.payment_method && (
            <div>
              <span className="text-ink-muted">{t('orderDetail.info.payment')}</span>
              <p className="font-medium text-ink flex items-center gap-1">
                <CreditCard
                  className="h-4 w-4 text-ink-muted"
                  aria-hidden="true"
                />
                {order.payment_method}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Status Timeline */}
      {!isCancelledOrRefunded && (
        <Card title={t('orderDetail.progress.title')}>
          <div
            className="flex items-center justify-between"
            role="list"
            aria-label={t('orderDetail.progress.ariaLabel')}
          >
            {TIMELINE_STEP_KEYS.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index <= currentStepIndex;
              const isCurrent = index === currentStepIndex;
              return (
                <div
                  key={step.key}
                  className="flex flex-col items-center flex-1 relative"
                  role="listitem"
                  aria-current={isCurrent ? 'step' : undefined}
                >
                  {/* Connector line */}
                  {index > 0 && (
                    <div
                      className={`absolute top-5 right-1/2 w-full h-0.5 -translate-y-1/2 ${
                        index <= currentStepIndex
                          ? 'bg-success-500'
                          : 'bg-line'
                      }`}
                      aria-hidden="true"
                    />
                  )}
                  <div
                    className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full ${
                      isCompleted
                        ? 'bg-success-100 text-success-700'
                        : 'bg-surface-muted text-ink-muted'
                    }`}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <span
                    className={`text-xs mt-2 text-center ${
                      isCompleted
                        ? 'font-medium text-success-700'
                        : 'text-ink-muted'
                    }`}
                  >
                    {t(`orderDetail.progress.steps.${step.labelKey}`)}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Tracking Information */}
      {order.tracking_number && (
        <Card>
          <div className="flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 p-4">
            <Truck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900">{t('orderDetail.tracking.heading')}</h4>
              {order.tracking_carrier && (
                <p className="text-sm text-blue-700 mt-1">
                  {t('orderDetail.tracking.carrierLabel')} <span className="font-medium">{order.tracking_carrier}</span>
                </p>
              )}
              <p className="text-sm text-blue-700 mt-0.5">
                {t('orderDetail.tracking.numberLabel')} <span className="font-mono font-medium">{order.tracking_number}</span>
              </p>
              <Link to={`/track/${order.order_number}`}
                className="inline-block mt-2 text-sm font-medium text-sage-700 hover:text-sage-800 hover:underline">
                {t('orderDetail.tracking.viewDetail')}
              </Link>
            </div>
          </div>
        </Card>
      )}

      {/* Shipments */}
      {(order as any).shipments?.length > 0 && !order.tracking_number && (
        <Card>
          {(order as any).shipments.map((s: any) => (
            <div key={s.id} className="flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 p-4 mb-2 last:mb-0">
              <Truck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-blue-900">{s.carrier?.toUpperCase()} - {s.service_name}</p>
                {s.tracking_number && (
                  <p className="text-sm text-blue-700 mt-0.5">
                    {t('orderDetail.tracking.shipmentTrackingLabel')} <span className="font-mono">{s.tracking_number}</span>
                    {s.tracking_url && (
                      <a href={s.tracking_url} target="_blank" rel="noopener"
                        className="ml-2 text-blue-600 hover:underline text-xs">{t('orderDetail.tracking.trackLink')}</a>
                    )}
                  </p>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Return Request */}
      {order.status === 'delivered' && (
        <Card>
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-soft">{t('orderDetail.returns.prompt')}</p>
            <Link to="/account/returns">
              <Button variant="outline" size="sm">{t('orderDetail.returns.cta')}</Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Cancelled / Refunded notice */}
      {isCancelledOrRefunded && (
        <Card>
          <div className="flex items-center gap-3 text-sm">
            <XCircle
              className="h-5 w-5 text-red-500 shrink-0"
              aria-hidden="true"
            />
            <p className="text-ink-soft">
              {t('orderDetail.cancelledNotice.intro')}{' '}
              <span className="font-semibold">
                {order.status === 'cancelled'
                  ? t('orderDetail.cancelledNotice.cancelled')
                  : t('orderDetail.cancelledNotice.refunded')}
              </span>
              {order.cancelled_at &&
                ` ${t('orderDetail.cancelledNotice.withDate', { date: formatDateShort(order.cancelled_at) })}`}
              .
            </p>
          </div>
        </Card>
      )}

      {/* Downloads available — shown when the order has any digital line items */}
      {order.items?.some((it: any) => it.is_digital) && (
        <Card>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Download className="h-5 w-5 text-ink-soft shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-medium text-ink">{t('orderDetail.digital.title')}</p>
                <p className="text-sm text-ink-muted">
                  {t('orderDetail.digital.description')}
                </p>
              </div>
            </div>
            <Link to="/account/downloads">
              <Button>{t('orderDetail.digital.openCta')}</Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Order Items */}
      <Card title={t('orderDetail.items.heading')}>
        <div className="divide-y divide-line">
          {order.items?.map((item: any) => (
            <div key={item.id} className="flex gap-4 py-4 first:pt-0 last:pb-0">
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt={item.product_name}
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md border border-line"
                />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-surface-muted rounded-md flex items-center justify-center border border-line">
                  <Package
                    className="h-6 w-6 text-ink-muted"
                    aria-hidden="true"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-ink truncate">
                  {item.product_name}
                </p>
                {item.variant_name && (
                  <p className="text-sm text-ink-muted">{item.variant_name}</p>
                )}
                <p className="text-sm text-ink-muted">{t('orderDetail.items.quantityLabel', { count: item.quantity })}</p>
                {item.is_digital && (
                  <p className="text-xs font-medium text-emerald-700 mt-1 flex items-center gap-1">
                    <Download className="h-3 w-3" aria-hidden="true" />
                    {t('orderDetail.digital.lineLabel')}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold text-ink">
                  {formatCurrency(Number(item.total || item.price * item.quantity))}
                </p>
                {item.quantity > 1 && (
                  <p className="text-xs text-ink-muted">
                    {t('orderDetail.items.eachPrice', { price: formatCurrency(Number(item.price)) })}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Order Totals */}
        <div className="border-t border-line mt-4 pt-4 space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-ink-muted">{t('orderDetail.totals.subtotal')}</span>
            <span className="text-ink">
              {formatCurrency(Number(order.subtotal ?? order.total))}
            </span>
          </div>
          {order.shipping_cost != null && (
            <div className="flex justify-between">
              <span className="text-ink-muted">{t('orderDetail.totals.shipping')}</span>
              <span className="text-ink">
                {Number(order.shipping_cost) === 0
                  ? t('orderDetail.totals.free')
                  : formatCurrency(Number(order.shipping_cost))}
              </span>
            </div>
          )}
          {order.discount_amount != null && Number(order.discount_amount) > 0 && (
            <div className="flex justify-between text-success-700">
              <span>{t('orderDetail.totals.discount')}</span>
              <span>-{formatCurrency(Number(order.discount_amount))}</span>
            </div>
          )}
          {order.tax_amount != null && (
            <div className="flex justify-between">
              <span className="text-ink-muted">{t('orderDetail.totals.tax')}</span>
              <span className="text-ink">
                {formatCurrency(Number(order.tax_amount))}
              </span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-base pt-2 border-t border-line">
            <span>{t('orderDetail.totals.total')}</span>
            <span>{formatCurrency(Number(order.total))}</span>
          </div>
        </div>
      </Card>

      {/* Addresses */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <MapPin
              className="h-4 w-4 text-ink-muted"
              aria-hidden="true"
            />
            <h4 className="font-semibold text-ink text-sm">
              {t('orderDetail.addresses.shipping')}
            </h4>
          </div>
          {order.shipping_address ? (
            <AddressBlock address={order.shipping_address} />
          ) : (
            <p className="text-sm text-ink-muted">{t('orderDetail.addresses.notAvailable')}</p>
          )}
        </Card>

        <Card>
          <div className="flex items-center gap-2 mb-3">
            <CreditCard
              className="h-4 w-4 text-ink-muted"
              aria-hidden="true"
            />
            <h4 className="font-semibold text-ink text-sm">
              {t('orderDetail.addresses.billing')}
            </h4>
          </div>
          {order.billing_address ? (
            <AddressBlock address={order.billing_address} />
          ) : (
            <p className="text-sm text-ink-muted">{t('orderDetail.addresses.sameAsShipping')}</p>
          )}
        </Card>
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        title={t('orderDetail.cancelModal.title')}
      >
        <p className="text-sm text-ink-soft mb-6">
          {t('orderDetail.cancelModal.bodyPrefix')}{' '}
          <span className="font-semibold">{t('orderDetail.cancelModal.bodyOrderNumber', { number: order.order_number })}</span>
          {t('orderDetail.cancelModal.bodySuffix')}
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => setIsCancelModalOpen(false)}
          >
            {t('orderDetail.cancelModal.keepOrder')}
          </Button>
          <Button
            variant="danger"
            onClick={handleCancelOrder}
            isLoading={isCancelling}
          >
            {t('orderDetail.cancelModal.confirm')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
